// src/web/client/src/pages/Orders.jsx
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useToast } from "@/components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import {
  Search,
  ShoppingBag,
  MessageSquare,
  Mail,
  Plus,
  Clock,
  Package,
  DollarSign,
  AlertCircle
} from "lucide-react";
// Pure grouping for the per-row activity tags (unit-tested):
// messages → { [order_id]: { sent, opened } }. Honest by construction —
// only status==='sent' counts, opened only among sent.
import { buildOrderActivity } from "@/utils/orderActivity";
import { format, formatDistanceToNow } from "date-fns";
// Simulate/create-order UI was removed: simulated orders are excluded from
// every merchant-facing number (data integrity), so the button had no honest
// value for a real merchant. Returns per-shop when dev-store detection lands.
import { authenticatedFetch, SubscriptionRequiredError } from "@/utils/authenticatedFetch";
import { Link } from "react-router-dom";

// Shop identity via the shared resolver (priority-3): URL ?shop= wins over
// localStorage, no silent demo fallback. This page's old private copy was a
// pre-#89 leftover that read storage-first AND fell back to demo-shop.
import { getShopDomain } from "@/utils/shopContext";

const API_BASE_URL = import.meta.env.VITE_API_URL || "https://api.ecomoria.com/api";

// `embedded`: hosted inside Customers → Orders (no header or page padding of its own).
export default function Orders({ embedded = false } = {}) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [shop, setShop] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderMessages, setOrderMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [orderStatus, setOrderStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState(null);
  // Populated when the backend returns 402 (no active subscription). We
  // render a friendly "choose a plan" screen instead of the generic
  // "Error Loading Orders" banner.
  const [subscriptionBlock, setSubscriptionBlock] = useState(null);
  // Per-order email activity for the list tags: { [order_id]: {sent, opened} }.
  // null = still loading OR unavailable → the tag slot renders NOTHING (a quiet
  // loading state — never a fake zero, never a wrong count).
  const [orderActivity, setOrderActivity] = useState(null);

  useEffect(() => {
    const shopDomain = getShopDomain();
    if (!shopDomain || shopDomain === 'undefined' || shopDomain === 'null' || shopDomain === '') {
      console.error("No valid shop domain - redirecting to onboarding");
      navigate('/onboarding');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const shopDomain = getShopDomain(); // תיקון - הגדרת המשתנה
      const isDemo = shopDomain === 'demo-shop.myshopify.com';
      
      // Demo data
     /* if (isDemo) {
        setOrders([
          {
            _id: 'demo-order-1',
            shop_domain: 'demo-shop.myshopify.com',
            order_id: 'DEMO-1001',
            status: 'completed',
            customer: {
              name: 'John Doe',
              email: 'john@demo.com'
            },
            products: [{
              name: 'Demo Product',
              price: 49.99,
              quantity: 2
            }],
            total: 99.99,
            created_at: new Date()
          },
          {
            _id: 'demo-order-2',
            shop_domain: 'demo-shop.myshopify.com',
            order_id: 'DEMO-1002',
            status: 'processing',
            customer: {
              name: 'Jane Smith',
              email: 'jane@demo.com'
            },
            products: [{
              name: 'Another Product',
              price: 149.99,
              quantity: 1
            }],
            total: 149.99,
            created_at: new Date(Date.now() - 86400000)
          }
        ]);
        
        setShop({
          _id: 'demo-shop-id',
          shop_domain: 'demo-shop.myshopify.com',
          shop_name: 'Demo Shop'
        });
        
        setIsLoading(false);
        return;
      }*/

      // Load shop data
      try {
        //const shopRes = await fetch(`${API_BASE_URL}/shops?shop_domain=${shopDomain}`);
        const shopRes = await authenticatedFetch(`${API_BASE_URL}/shops?shop_domain=${shopDomain}`);
        if (!shopRes.ok) {
          if (shopRes.status === 404) {
            throw new Error("Shop not found. Please reconnect your store.");
          }
          throw new Error(`Failed to load shop data: ${shopRes.statusText}`);
        }
        const shopData = await shopRes.json();
        const currentShop = Array.isArray(shopData) ? shopData[0] : shopData;
        
        if (!currentShop) {
          throw new Error("Shop configuration not found");
        }
        
        setShop(currentShop);
      } catch (shopError) {
        console.error("Shop loading error:", shopError);
        throw shopError;
      }

      // Load orders
      try {
        //const ordersRes = await fetch(`${API_BASE_URL}/orders?shop_domain=${shopDomain}&sort=-created_at`);
        const ordersRes = await authenticatedFetch(`${API_BASE_URL}/orders?shop_domain=${shopDomain}&sort=-created_at`);
        if (!ordersRes.ok) {
          if (ordersRes.status === 404) {
            setOrders([]);
            return;
          }
          throw new Error(`Failed to load orders: ${ordersRes.statusText}`);
        }
        
        const ordersData = await ordersRes.json();
       /* const validOrders = Array.isArray(ordersData) 
          ? ordersData.filter(order => order && order._id)
          : [];*/
          const validOrders = Array.isArray(ordersData) 
  ? ordersData.map(order => ({
      ...order,
      order_id: order.order_id || order.shopify_order_id || order._id,
      status: order.status || order.financial_status || 'pending',
      created_at: order.created_at || order.shopify_created_at,
      customer: {
        name: order.customer?.name || `${order.customer?.first_name || ''} ${order.customer?.last_name || ''}`.trim() || 'Guest',
        // No fabrication: an order without an email shows "No email on file"
        // at the display site — never an invented address. Display-only value
        // (verified: this screen has zero mutations; the only other consumer
        // is the client-side search filter, which handles null safely).
        email: order.customer?.email || null
      },
      total: order.total || order.total_price || 0
    }))
  : [];
        
        setOrders(validOrders);

        // Activity tags load in the background — deliberately NOT awaited, so
        // the orders render immediately and a tag failure can't block them.
        if (validOrders.length > 0) {
          loadOrderActivity(shopDomain);
        }

        if (validOrders.length > 0) {
          setSelectedOrder(validOrders[0]);
          await loadOrderMessages(validOrders[0]);
        }
        
      } catch (ordersError) {
        console.error("Orders loading error:", ordersError);
        setOrders([]);
        
        if (!isDemo) {
          toast({
            title: "Notice",
            description: "No orders found. Create your first order to get started!",
            variant: "info"
          });
        }
      }
      
    } catch (error) {
      console.error("Error loading data:", error);

      // 402 — no active subscription. Show the friendly "choose a plan"
      // card instead of the red error banner.
      if (error instanceof SubscriptionRequiredError) {
        setSubscriptionBlock({
          code: error.code,
          message: error.message,
          status: error.subscriptionStatus,
        });
        setIsLoading(false);
        return;
      }

      setError(error.message);

      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      
      if (error.message.includes("shop configuration") || error.message.includes("reconnect")) {
        setTimeout(() => navigate('/onboarding'), 2000);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch the shop's messages (existing GET /messages — no new endpoint) and
  // group them client-side for the per-row tags. Fire-and-forget from loadData:
  // a failure here can never break or slow the orders themselves.
  //
  // Pagination is bounded and honest:
  //  - We deliberately do NOT rely on the X-Page-Has-More header — the XHR
  //    wrapper in authenticatedFetch only propagates Content-Type, so custom
  //    headers are dropped (pre-existing issue, reported separately). A page
  //    shorter than the limit is the reliable end-of-data signal.
  //  - If MAX_PAGES is exhausted with data still remaining, we show NO tags at
  //    all rather than counts we know may be incomplete.
  const ACTIVITY_PAGE_LIMIT = 200; // the backend's clamp ceiling
  const ACTIVITY_MAX_PAGES = 10;   // 2,000 messages — far above current scale
  const loadOrderActivity = async (shopDomain) => {
    try {
      const all = [];
      for (let page = 0; page < ACTIVITY_MAX_PAGES; page++) {
        const res = await authenticatedFetch(
          `${API_BASE_URL}/messages?shop_domain=${shopDomain}&exclude_internal=true&limit=${ACTIVITY_PAGE_LIMIT}&skip=${page * ACTIVITY_PAGE_LIMIT}`
        );
        if (!res.ok) return; // quiet: no tags beats wrong tags
        const batch = await res.json();
        if (!Array.isArray(batch)) return;
        all.push(...batch);
        if (batch.length < ACTIVITY_PAGE_LIMIT) {
          setOrderActivity(buildOrderActivity(all));
          return;
        }
      }
      // Cap hit with more data likely remaining — counts would be incomplete,
      // so stay quiet (orderActivity remains null, no tags render).
    } catch (e) {
      // Quiet failure — the orders screen works exactly as before.
    }
  };

  const loadOrderMessages = async (order) => {
    if (!order || !order._id) return;
    
    try {
      const shopDomain = getShopDomain();
      const res = await authenticatedFetch(
        `${API_BASE_URL}/messages?shop_domain=${shopDomain}&order_id=${order._id || order.id}`
      );
      
      if (res.ok) {
        const messages = await res.json();
        setOrderMessages(Array.isArray(messages) ? messages : []);
      } else {
        setOrderMessages([]);
      }
    } catch (error) {
      console.error("Error loading messages:", error);
      setOrderMessages([]);
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
      case 'fulfilled':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredOrders = orders.filter(order => {
    if (!order) return false;
    
    const matchesStatus = orderStatus === "all" || order.status === orderStatus;
    const matchesSearch = searchQuery === "" || 
      order._id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.customer?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.customer?.email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesStatus && matchesSearch;
  });

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading orders...</p>
        </div>
      </div>
    );
  }

  if (subscriptionBlock) {
    // Friendly "choose a plan" CTA — used for any of pending / expired /
    // cancelled. The message text already comes status-specific from the
    // backend (PLAN_SELECTION_REQUIRED / TRIAL_EXPIRED /
    // SUBSCRIPTION_CANCELLED), so the screen says the right thing for
    // each state without us needing to branch in the UI.
    return (
      <div className="p-6">
        <Card className="max-w-md mx-auto">
          <CardContent className="pt-6">
            <div className="text-center">
              <Package className="w-12 h-12 text-indigo-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">
                {subscriptionBlock.code === 'TRIAL_EXPIRED'
                  ? 'Your free trial has ended'
                  : subscriptionBlock.code === 'SUBSCRIPTION_CANCELLED'
                    ? 'Your subscription was cancelled'
                    : 'Choose a plan to see your orders'}
              </h3>
              <p className="text-gray-600 mb-6">{subscriptionBlock.message}</p>
              <Link to="/billing">
                <Button>Go to Billing</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card className="max-w-md mx-auto">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Error Loading Orders</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <Button onClick={loadData} variant="outline">
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  return (
    <div className="p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Orders</h1>
            {/* Honest subtitle: this screen is read-only (no fulfill/cancel/
                refund — Shopify does that). Its unique value is Ecomoria's
                per-order email activity, so the header says exactly that. */}
            <p className="text-gray-500">
              See the emails Ecomoria sent for every order
            </p>
          </div>
          
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  type="text"
                  placeholder="Search orders..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <ToggleGroup 
                type="single" 
                value={orderStatus} 
                onValueChange={setOrderStatus}
              >
                <ToggleGroupItem value="all">All</ToggleGroupItem>
                <ToggleGroupItem value="pending">Pending</ToggleGroupItem>
                <ToggleGroupItem value="processing">Processing</ToggleGroupItem>
                <ToggleGroupItem value="completed">Completed</ToggleGroupItem>
              </ToggleGroup>
            </div>
          </CardContent>
        </Card>

        {/* Main Content */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Orders List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Recent Orders</span>
                <Badge variant="secondary">
                  {filteredOrders.length} {filteredOrders.length === 1 ? 'order' : 'orders'}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {filteredOrders.length === 0 ? (
                <div className="text-center py-8">
                  <ShoppingBag className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No orders found</p>
                  {orders.length === 0 && (
                    <p className="text-sm text-gray-400 mt-2">
                      Orders appear here automatically as customers buy from your store.
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredOrders.map((order) => (
                    <div
                      key={order._id}
                      onClick={() => {
                        setSelectedOrder(order);
                        loadOrderMessages(order);
                      }}
                      className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                        selectedOrder?._id === order._id 
                          ? 'border-indigo-600 bg-indigo-50' 
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="font-medium">#{order.order_number || order.order_id || 'N/A'}</p>
                          <p className="text-sm text-gray-600">
                            {order.customer?.name || 'Guest'}
                          </p>
                        </div>
                        <Badge className={getStatusColor(order.status)}>
                          {order.status || 'Unknown'}
                        </Badge>
                      </div>
                      
                      <div className="flex justify-between items-end">
                        <div className="text-sm text-gray-500">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {order.created_at ? formatDistanceToNow(new Date(order.created_at), { addSuffix: true }) : 'Unknown'}
                        </div>
                        <p className="font-semibold">
                          ${order.total?.toFixed(2) || '0.00'}
                        </p>
                      </div>

                      {/* Activity tag — Ecomoria's value at a glance, kept
                          secondary to the order number / total. Renders ONLY
                          once the activity map loaded (null = quiet loading /
                          unavailable — never a fake zero, never a wrong count). */}
                      {orderActivity && (
                        <div className="mt-2 pt-2 border-t border-gray-100">
                          {orderActivity[order._id]?.sent > 0 ? (
                            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600/80">
                              <Mail className="w-3 h-3" />
                              {orderActivity[order._id].sent} sent · {orderActivity[order._id].opened} opened
                            </span>
                          ) : (
                            <span className="text-[11px] text-gray-400">No emails yet</span>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Order Details */}
          <Card>
            <CardHeader>
              {/* Dynamic title: with the panel now led by Ecomoria's activity,
                  a static "Order Details" title would duplicate the secondary
                  "Order details" block below. The order number is existing
                  data (already shown in the list), not new copy. */}
              <CardTitle>
                {selectedOrder
                  ? `Order #${selectedOrder.order_number || selectedOrder.order_id || ''}`
                  : 'Order Details'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedOrder ? (
                <div className="space-y-5">
                  {/* LEAD — Ecomoria's per-order activity. This is the one thing
                      Shopify can't show, so it comes first and reads loudest.
                      Same data, same fetch (loadOrderMessages) — reorder +
                      restyle only. */}
                  <div>
                    <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900 mb-3">
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-indigo-100">
                        <MessageSquare className="w-4 h-4 text-indigo-600" />
                      </span>
                      What Ecomoria did for this order
                      {orderMessages.length > 0 && (
                        <Badge variant="secondary" className="ml-1">
                          {orderMessages.length}
                        </Badge>
                      )}
                    </h3>
                    {orderMessages.length > 0 ? (
                      <div className="space-y-2">
                        {orderMessages.map((message, index) => (
                          <div key={index} className="border border-indigo-100 rounded-lg p-3 bg-indigo-50/40">
                            <div className="flex justify-between items-start mb-1">
                              <span className="text-sm font-medium capitalize">
                                {message.type?.replace('_', ' ') || 'Message'}
                              </span>
                              <span className="text-xs text-gray-500">
                                {message.sent_date ? format(new Date(message.sent_date), "MMM d, HH:mm") : 'N/A'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600">{message.content || 'No content'}</p>
                            <div className="flex gap-2 mt-2">
                              <Badge variant={message.status === 'sent' ? 'success' : 'secondary'}>
                                {message.status || 'Unknown'}
                              </Badge>
                              {message.opened && (
                                <Badge variant="outline" className="text-green-600">
                                  Opened
                                </Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      /* Honest empty state — no activity is stated plainly,
                         never dressed up. */
                      <p className="text-sm text-gray-400">
                        No emails sent for this order yet.
                      </p>
                    )}
                  </div>

                  {/* SECONDARY — order context. Shopify already presents this
                      better; kept quiet, for reference while reading the
                      activity above. Same fields as before, demoted styling. */}
                  <div className="pt-4 border-t border-gray-100 space-y-4">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                      Order details
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-500">Name:</span>
                        <span className="text-gray-700">{selectedOrder.customer?.name || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Email:</span>
                        {selectedOrder.customer?.email ? (
                          <span className="text-gray-700">{selectedOrder.customer.email}</span>
                        ) : (
                          <span className="text-gray-400">No email on file</span>
                        )}
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Phone:</span>
                        <span className="text-gray-700">{selectedOrder.customer?.phone || 'N/A'}</span>
                      </div>
                    </div>

                    <div>
                      <div className="space-y-2">
                        {selectedOrder.products?.map((item, index) => (
                          <div key={index} className="flex justify-between text-sm">
                            <span className="text-gray-600">
                              {item.name} x {item.quantity || 1}
                            </span>
                            <span className="font-medium text-gray-700">${item.price}</span>
                          </div>
                        )) || <p className="text-sm text-gray-500">No items</p>}
                      </div>
                      <div className="border-t border-gray-100 pt-2 mt-2">
                        <div className="flex justify-between text-sm font-medium text-gray-700">
                          <span>Total</span>
                          <span>${selectedOrder.total?.toFixed(2) || '0.00'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">Select an order to view details</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

      </div>
    </div>
  );
}
